import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from '../../auth/Auth';
import { getPointTasksWithProgressAndStats } from '../../auth/_requests';
import type { Offer, OfferStats, PointCategoryName, PointTaskCode } from '../utils/types';

const EMPTY_STATS: OfferStats = { totalTasks: 0, totalCompleted: 0, categories: [] };

interface PointTasksContextProps {
  /** All point tasks for the current user */
  tasks: Offer[];
  /** Aggregate stats across all tasks */
  stats: OfferStats;
  loading: boolean;
  error: string | undefined;
  /** Manually re-fetch tasks (e.g. after a payment or reward event) */
  refetch: () => Promise<void>;
  /** Return only tasks matching the given task codes */
  getTasksByCode: (codes: PointTaskCode[]) => Offer[];
  /** Return only tasks belonging to a specific category */
  getTasksByCategory: (categoryName: PointCategoryName) => Offer[];
}

const PointTasksContext = createContext<PointTasksContextProps>({
  tasks: [],
  stats: EMPTY_STATS,
  loading: false,
  error: undefined,
  refetch: async () => {},
  getTasksByCode: () => [],
  getTasksByCategory: () => [],
});

export const usePointTasksContext = () => useContext(PointTasksContext);

export const PointTasksProvider = ({ children }: { children: ReactNode }) => {
  const { auth } = useAuth();
  const [tasks, setTasks] = useState<Offer[]>([]);
  const [stats, setStats] = useState<OfferStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(() => Boolean(auth?.data.token));
  const [error, setError] = useState<string | undefined>(undefined);

  const fetchPointTasks = useCallback(async () => {
    if (!auth?.data.token) {
      setTasks([]);
      setStats(EMPTY_STATS);
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const res = await getPointTasksWithProgressAndStats(auth.data.token);
      setTasks(res.tasks);
      setStats(res.stats);
    } catch (e) {
      console.error('Error fetching point tasks:', e);
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [auth?.data.token]);

  useEffect(() => {
    fetchPointTasks();
  }, [fetchPointTasks]);

  const getTasksByCode = useCallback(
    (codes: PointTaskCode[]) => tasks.filter((t) => codes.includes(t.code)),
    [tasks]
  );

  const getTasksByCategory = useCallback(
    (categoryName: PointCategoryName) => tasks.filter((t) => t.pointCategoryName === categoryName),
    [tasks]
  );

  const value = useMemo(
    () => ({
      tasks,
      stats,
      loading,
      error,
      refetch: fetchPointTasks,
      getTasksByCode,
      getTasksByCategory,
    }),
    [tasks, stats, loading, error, fetchPointTasks, getTasksByCode, getTasksByCategory]
  );

  return <PointTasksContext.Provider value={value}>{children}</PointTasksContext.Provider>;
};
